/**
 * Mention Read Tracking Hook
 * Auto-marks mentions as read when user scrolls past them
 * Uses IntersectionObserver with 50% viewport threshold
 */

import { useEffect, useRef } from 'react';
import { useMarkMentionRead } from './useMentions';
import { useAppStore } from '@/store/app.store';

interface UseMentionReadTrackingOptions {
  /**
   * Message ID to track
   */
  messageId: string;

  /**
   * Whether this message contains a mention of the current user
   */
  hasMention: boolean;

  /**
   * Whether the mention is already read
   */
  isRead: boolean;

  /**
   * Mention ID (for marking as read)
   */
  mentionId?: string;

  /**
   * Viewport threshold (0-1), default 0.5 (50%)
   */
  threshold?: number;
}

export const useMentionReadTracking = ({
  messageId,
  hasMention,
  isRead,
  mentionId,
  threshold = 0.5,
}: UseMentionReadTrackingOptions) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const hasBeenMarkedRef = useRef(false);
  const markMentionReadMutation = useMarkMentionRead();
  const { markMentionRead: markMentionReadInStore } = useAppStore();

  useEffect(() => {
    // Skip if:
    // - No mention on this message
    // - Already read
    // - Already marked in this session
    // - No mention ID
    if (!hasMention || isRead || hasBeenMarkedRef.current || !mentionId) {
      return;
    }

    const element = elementRef.current;
    if (!element) return;

    // Create IntersectionObserver
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Check if element is at least 50% visible
          if (entry.isIntersecting && entry.intersectionRatio >= threshold) {
            // Mark as read (prevent duplicate calls)
            if (!hasBeenMarkedRef.current) {
              hasBeenMarkedRef.current = true;

              console.log('📖 Auto-marking mention as read:', messageId, mentionId);

              // Mark in backend
              markMentionReadMutation.mutate(mentionId);

              // Mark in local store
              const conversationId = entry.target.getAttribute('data-conversation-id');
              if (conversationId) {
                markMentionReadInStore(conversationId, mentionId);
              }
            }
          }
        });
      },
      {
        threshold: [threshold], // Trigger when 50% visible
        rootMargin: '0px', // No margin adjustment
      }
    );

    // Start observing
    observer.observe(element);

    // Cleanup
    return () => {
      observer.disconnect();
    };
  }, [
    messageId,
    hasMention,
    isRead,
    mentionId,
    threshold,
    markMentionReadMutation,
    markMentionReadInStore,
  ]);

  return elementRef;
};
